/**
 * Anmeldefrist-Auflösung.
 *
 * `school.registrationDeadline` ist ein einzelner Timestamp pro Schule — Tag,
 * Monat und Jahr fest. Solange man sich nur für das nächste Schuljahr anmelden
 * konnte, passte das; seit Eltern 1–2 Jahre im Voraus wählen können (#746),
 * zeigte die App bei einem künftigen Schuljahr eine längst verstrichene Frist.
 *
 * Fachliche Festlegung: Tag und Monat sind fix (AWO ändert sie nicht), das Jahr
 * ergibt sich aus dem Schuljahr. Die Frist für 2029/30 liegt also am selben
 * Tag im Jahr 2029 — Schuljahre starten am 01.08., die Frist davor.
 */

/** Firestore-Timestamp, per Duck-Typing (Frontend- und Admin-SDK). */
interface TimestampLike {
  toDate?: () => Date;
  seconds?: number;
}

function toDate(value: TimestampLike | null | undefined): Date | null {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  return null;
}

/**
 * Frist der Schule auf ein konkretes Schuljahr bezogen — Tag und Monat aus der
 * hinterlegten Frist, Jahr aus dem Startjahr des Schuljahrs.
 */
export function resolveRegistrationDeadline(
  registrationDeadline: TimestampLike | null | undefined,
  schoolYear: number,
): Date | null {
  const base = toDate(registrationDeadline);
  if (!base) return null;
  return new Date(schoolYear, base.getMonth(), base.getDate());
}

/**
 * Nächste noch nicht verstrichene Frist — für Anzeigen ohne Schuljahr-Bezug
 * (z.B. die öffentliche Schulseite). Liegt die Frist des laufenden Schuljahrs
 * bereits in der Vergangenheit, gilt die des kommenden.
 */
export function getNextRegistrationDeadline(
  registrationDeadline: TimestampLike | null | undefined,
  currentSchoolYear: number,
  now: Date = new Date(),
): Date | null {
  const current = resolveRegistrationDeadline(
    registrationDeadline,
    currentSchoolYear,
  );
  if (!current) return null;
  if (current >= now) return current;
  return resolveRegistrationDeadline(registrationDeadline, currentSchoolYear + 1);
}
